import React, { useState } from 'react';
import { useDocumentTitle } from '../useDocumentTitle';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

type ServiceResult = { serviceable: boolean; city: string | null; area: string | null } | null;

const GOALS = ['Weight Loss', 'Muscle Gain', 'Gut Health', 'Diabetes / Metabolic', 'General Wellness'];

export const GetStartedPage: React.FC = () => {
  useDocumentTitle(
    'Check Delivery & Get Started | The Good Food Way',
    'Check if we deliver to your pincode and tell us about your goals.'
  );

  // ── Pincode serviceability check ──
  const [pincode, setPincode] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<ServiceResult>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  const checkPincode = async () => {
    const pin = pincode.trim();
    if (!/^\d{4,10}$/.test(pin)) { setCheckError('Please enter a valid pincode.'); return; }
    setCheckError(null);
    setResult(null);
    setChecking(true);
    try {
      const res = await fetch(`${API_BASE_URL}/public/serviceability?pincode=${encodeURIComponent(pin)}`);
      if (!res.ok) throw new Error('Could not check right now.');
      setResult(await res.json());
    } catch (err) {
      setCheckError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setChecking(false);
    }
  };

  // ── Questionnaire ──
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', pincode: '', goal: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Please tell us your name.'); return; }
    if (!form.phone.trim() && !form.email.trim()) { setFormError('Please give a phone or email so we can reach you.'); return; }
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/public/questionnaire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          city: form.city.trim() || null,
          pincode: form.pincode.trim() || null,
          goal: form.goal || null,
          message: form.message.trim() || null,
        }),
      });
      if (!res.ok) throw new Error('Submission failed. Please try again.');
      setSubmitted(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.85rem 1rem', borderRadius: '12px',
    border: '1px solid #e0e0e0', fontSize: '1rem', fontFamily: 'inherit',
  };

  return (
    <div className="marketing-page">
      <section style={{ padding: '7rem 0 3rem', textAlign: 'center', background: 'var(--color-cream, #FFF8F0)' }}>
        <div className="container" style={{ maxWidth: 720 }}>
          <h1 style={{ fontSize: '2.6rem', color: 'var(--color-dark-green)', marginBottom: '0.75rem' }}>
            Let&apos;s Get You Started
          </h1>
          <p style={{ fontSize: '1.1rem', color: 'var(--color-grey)', marginBottom: 0 }}>
            First, check if we deliver to you. Then tell us a little about your goals.
          </p>
        </div>
      </section>

      {/* Pincode checker */}
      <section style={{ padding: '3rem 0', backgroundColor: '#fff' }}>
        <div className="container" style={{ maxWidth: 620 }}>
          <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 20, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: '1.6rem', color: 'var(--color-dark-green)', marginBottom: '1rem', textAlign: 'center' }}>
              Do we deliver to you?
            </h2>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input
                style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                type="text" inputMode="numeric" placeholder="Enter your pincode"
                value={pincode}
                onChange={e => setPincode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') checkPincode(); }}
              />
              <button
                onClick={checkPincode} disabled={checking}
                style={{ background: 'var(--color-orange)', color: '#fff', border: 'none', borderRadius: 50, padding: '0.85rem 2rem', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
              >
                {checking ? 'Checking…' : 'Check'}
              </button>
            </div>
            {checkError && <p style={{ color: '#d32f2f', marginTop: '0.75rem', marginBottom: 0 }}>{checkError}</p>}
            {result && (
              <div style={{
                marginTop: '1rem', padding: '1rem 1.25rem', borderRadius: 12,
                background: result.serviceable ? '#E8F5E9' : '#FFF3E0',
                color: result.serviceable ? '#2E7D32' : '#E65100',
              }}>
                {result.serviceable
                  ? <>🎉 Great news — we deliver to <strong>{pincode}</strong>{result.city ? ` (${result.city})` : ''}! Fill in the form below and we&apos;ll get you set up.</>
                  : <>We&apos;re not in <strong>{pincode}</strong> just yet. Leave your details below and we&apos;ll notify you the moment we launch there.</>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Questionnaire */}
      <section style={{ padding: '1rem 0 5rem', backgroundColor: '#fff' }}>
        <div className="container" style={{ maxWidth: 620 }}>
          <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 20, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h2 style={{ fontSize: '1.6rem', color: 'var(--color-dark-green)', marginBottom: '0.5rem' }}>Thank you!</h2>
                <p style={{ color: 'var(--color-grey)', margin: 0 }}>We&apos;ve got your details and will be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <h2 style={{ fontSize: '1.6rem', color: 'var(--color-dark-green)', marginBottom: '1.25rem', textAlign: 'center' }}>
                  Tell us about your goals
                </h2>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <input style={inputStyle} placeholder="Your name *" value={form.name} onChange={set('name')} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <input style={inputStyle} placeholder="Phone" value={form.phone} onChange={set('phone')} />
                    <input style={inputStyle} type="email" placeholder="Email" value={form.email} onChange={set('email')} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <input style={inputStyle} placeholder="City" value={form.city} onChange={set('city')} />
                    <input style={inputStyle} placeholder="Pincode" value={form.pincode} onChange={set('pincode')} />
                  </div>
                  <select style={inputStyle} value={form.goal} onChange={set('goal')}>
                    <option value="">What&apos;s your main goal?</option>
                    {GOALS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} placeholder="Anything else you'd like us to know?" value={form.message} onChange={set('message')} />
                  {formError && <p style={{ color: '#d32f2f', margin: 0 }}>{formError}</p>}
                  <button
                    type="submit" disabled={submitting}
                    style={{ background: 'var(--color-orange)', color: '#fff', border: 'none', borderRadius: 50, padding: '1rem 2rem', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}
                  >
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
